// What a reputation read MEANS, derived once.
//
// Every surface that shows reputation has to answer the same question: is this
// still loading, did the read fail, is there no on-chain identity, is this an
// untouched starting score, or is it earned history? Those are five different
// facts and only the last two are a number worth rendering.
//
// Deriving that per component is how they drift. Before this module existed the
// three call sites each rolled their own and two were wrong: the admin panel
// stated "Not registered on this network" while the read was still in flight
// (`!data` is true during loading), and the dashboard collapsed a failed read
// into the same em dash it used for "no identity". Interpretation belongs next
// to the data, not in the JSX.

import type { ReputationDetail } from "@/lib/types";
import { REPUTATION_DEFAULT_SCORE } from "@/lib/reputationScale";
import { RpcChainMismatchError } from "@/lib/chainGuard";

/** `DEFAULT_SCORE` as the UI sees it: 5000 basis points → 50. */
export const REPUTATION_BASELINE = REPUTATION_DEFAULT_SCORE / 100;

export type ReputationView =
  /** Read in flight. Say nothing — not "none", not zero. */
  | { status: "loading" }
  /**
   * The read failed. Distinct from having no reputation. `wrongChain` means the
   * RPC answered as a different chain, a deployment config fault that retrying
   * will not fix; `failed` is anything else.
   */
  | { status: "error"; reason: "wrongChain" | "failed" }
  /** No wallet, or never registered on the active chain. */
  | { status: "none" }
  /** Registered, but the ledger has never recorded anything: a flat 50. */
  | { status: "baseline"; detail: ReputationDetail }
  /** Real history. */
  | { status: "active"; detail: ReputationDetail };

/**
 * True when the ledger has recorded anything at all beyond initialization.
 *
 * Counters alone are NOT sufficient. `recordDisputeWin` adjusts reliability
 * without incrementing any counter, and `recordDisputeLoss` increments only
 * `tasksDisputed` — so a worker can carry a real, altered score while
 * `tasksCompleted` and `questsCompleted` are both still zero. Checking the axes
 * as well is what stops the card telling someone who just lost a dispute that
 * their 47 is an untouched "starting score".
 */
function hasHistory(d: ReputationDetail): boolean {
  if (
    d.tasksCompleted > 0 ||
    d.questsCompleted > 0 ||
    d.questsCreated > 0 ||
    d.tasksDisputed > 0
  ) {
    return true;
  }
  return (
    d.overall !== REPUTATION_BASELINE ||
    d.completionRate !== REPUTATION_BASELINE ||
    d.qualityScore !== REPUTATION_BASELINE ||
    d.speedScore !== REPUTATION_BASELINE ||
    d.reliability !== REPUTATION_BASELINE
  );
}

/**
 * Collapse a react-query result into the one thing a component should switch on.
 *
 * Order matters: `isPending` is checked before `!data`, because react-query
 * leaves `data` undefined while the first read is in flight and "undefined"
 * there means "not yet", never "nothing".
 */
export function toReputationView(result: {
  isPending: boolean;
  isError: boolean;
  error?: unknown;
  data: ReputationDetail | null | undefined;
}): ReputationView {
  if (result.isPending) return { status: "loading" };
  if (result.isError) {
    return {
      status: "error",
      reason: result.error instanceof RpcChainMismatchError ? "wrongChain" : "failed",
    };
  }
  if (!result.data) return { status: "none" };
  return hasHistory(result.data)
    ? { status: "active", detail: result.data }
    : { status: "baseline", detail: result.data };
}

/** The detail to render, or null when there is nothing to render. */
export function reputationDetail(view: ReputationView): ReputationDetail | null {
  return view.status === "baseline" || view.status === "active"
    ? view.detail
    : null;
}
